import { useEffect, useRef, useState, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTrefferListe } from "@/lib/trefferListe";
import { api } from "@/lib/api";
import { useTerminMeldung, terminGeaendert, terminPlanen, useKalenderFenster, projektImHauptfenster, werkbankAdresse } from "@/lib/windowSync";
import { useF1Help, hilfeOeffnen } from "@/lib/useF1Help";import { toast } from "sonner";
import { TerminSendDialog } from "@/components/TerminSendDialog";
import { terminBestaetigen } from "@/lib/terminGo";
import { VorlagenPicker } from "@/components/VorlagenPicker";
import TitleInputWithVorlagen from "@/components/TitleInputWithVorlagen";
import { TextareaWithAI } from "@/components/TextareaWithAI";
import {
  Calendar, Plus, Trash2, X, MapPin, User as UserIcon, Folder, Briefcase, HardHat,
  CheckCircle2, Clock, RefreshCw, Filter, AlertTriangle, ChevronRight, XCircle, Search,
  GripVertical, Wrench, ChevronLeft, ExternalLink, List as ListIcon, CalendarDays, HelpCircle, Bell,
} from "lucide-react";
import { colorForUser, initialsOf, MonteurAvatar, ladeMonteurFarben, monteurName } from "@/lib/avatarUtils";
import EinsatzModal from "@/components/EinsatzModal";
import MonteurMehrfachAuswahl from "@/components/MonteurMehrfachAuswahl";
import TerminKalender from "./TerminKalender";
import PlanungsLeiste from "./PlanungsLeiste";
import { usePlanung } from "./planungLogik";
import { ladeAbwesenheiten } from "./abwesenheiten";
import { TerminSeitenleiste } from "./TerminSeitenleiste";
import { typBezeichnung } from "@/lib/terminTyp";
import ErinnerungenDialog from "./ErinnerungenDialog";
import { findeKonflikte, konfliktText, monteureVon, terminZeiten } from "./kalenderLogik";

// Liste | Kalender: Wahl wird gemerkt (nur als Bequemlichkeit, ohne Speicher geht es auch)
const MODUS_KEY = "termine_modus";
const gemerkterModus = () => { try { return localStorage.getItem(MODUS_KEY) === "kalender" ? "kalender" : "liste"; } catch { return "liste"; } };

const STATUS_LABEL = {
  wartet_auf_go: "Wartet auf GO",
  bestaetigt: "Bestätigt (bereit für Kalender)",
  im_kalender: "Im Kalender",
  abgesagt: "Abgesagt",
};

const STATUS_STYLES = {
  wartet_auf_go: { cls: "bg-amber-50 dark:bg-amber-950/40 text-amber-800 dark:text-amber-200 border-amber-200 dark:border-amber-900", icon: AlertTriangle },
  bestaetigt: { cls: "bg-blue-50 dark:bg-blue-950/40 text-blue-800 dark:text-blue-200 border-blue-200 dark:border-blue-900", icon: Clock },
  im_kalender: { cls: "bg-emerald-50 dark:bg-emerald-950/40 text-emerald-800 dark:text-emerald-200 border-emerald-200 dark:border-emerald-900", icon: CheckCircle2 },
  abgesagt: { cls: "bg-red-50 dark:bg-red-950/40 text-red-700 dark:text-red-200 border-red-200 dark:border-red-900", icon: XCircle },
};


const fmtDate = (s) => {
  if (!s) return "";
  try { return new Date(s).toLocaleString("de-DE", { dateStyle: "medium", timeStyle: "short" }); }
  catch { return s; }
};

export default function ModuleTerminePage({ kalenderFenster = false, vollflaeche = false }) {   // vollflaeche: Route /module/kalender (Kalender im Hauptfenster, ohne Seitenmenü)
  const voll = kalenderFenster || vollflaeche;   // volle Fläche, kompakter Kopf   // kalenderFenster: Route /kalender (großes eigenes Fenster)
  useF1Help("hilfe_termine");
  const [termine, setTermine] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState("");
  const [enriched, setEnriched] = useState(null);
  const [sendingTermin, setSendingTermin] = useState(null);
  const [dragIndex, setDragIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [selectedMonteure, setSelectedMonteure] = useState(new Set());
  const [modus, setModus] = useState(voll ? "kalender" : gemerkterModus);               // "liste" | "kalender" (A-027 S1)
  const [erinnerungenOffen, setErinnerungenOffen] = useState(false);   // A-027 S3: Einstellungen der Erinnerungen
  const [abwesenheiten, setAbwesenheiten] = useState([]);           // Urlaub/Krank, nur wo Mitarbeiter und Benutzer eindeutig zusammenpassen
  const [einsatzCtx, setEinsatzCtx] = useState(null);  // {kundeId, projektId?} — zentrales EinsatzModal

  // Stammdaten für Auswahl
  const [kunden, setKunden] = useState([]);
  const [projekte, setProjekte] = useState([]);
  const [aufgaben, setAufgaben] = useState([]);
  const [mitarbeiter, setMitarbeiter] = useState([]);
  // Such-zuerst-Schema (Ralph 12.05.2026)
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedTarget, setSelectedTarget] = useState(null);
  // Trefferliste: schliesst nicht mehr vor dem Klick, Tastatur Pfeil runter + Enter (07.10.2026)
  const { offen: searchFocused, boxProps: sucheBox, inputProps: sucheInput, listProps: sucheListe } = useTrefferListe();

  const load = async (leise) => {
    if (leise !== true) setLoading(true);   // leise = Selbst-Aktualisierung ohne Ladeanzeige
    try {
      await ladeMonteurFarben(true);   // Farbe der Monteure aus den Mitarbeitern (eine zentrale Stelle, avatarUtils)
      const [list, k, p, a, m] = await Promise.all([
        api.get("/module-termine", { params: { status: filterStatus } }),
        kunden.length ? Promise.resolve({ data: kunden }) : api.get("/modules/kunden/data").catch(() => ({ data: [] })),
        projekte.length ? Promise.resolve({ data: projekte }) : api.get("/module-projekte").catch(() => ({ data: [] })),
        api.get("/module-aufgaben").catch(() => ({ data: [] })),   // immer frisch: der Kalender zeigt Aufgaben am Fälligkeitstag
        mitarbeiter.length ? Promise.resolve({ data: mitarbeiter }) : api.get("/module-aufgaben/mitarbeiter").catch(() => ({ data: [] })),
      ]);
      setTermine(Array.isArray(list.data) ? list.data : []);
      if (!kunden.length) setKunden(Array.isArray(k.data) ? k.data : []);
      if (!projekte.length) setProjekte(Array.isArray(p.data) ? p.data : []);
      setAufgaben(Array.isArray(a.data) ? a.data : []);
      if (!mitarbeiter.length) setMitarbeiter(Array.isArray(m.data) ? m.data : []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Termine konnten nicht geladen werden");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [filterStatus]); // eslint-disable-line
  useTerminMeldung(() => load(true));   // anderes Fenster hat gespeichert -> neu laden

  // Kalender-Fenster hält sich selbst aktuell: beim Fokus und alle 2 Minuten – nicht, solange ein Dialog offen ist oder gezogen wird
  const belegt = useRef(false);
  belegt.current = !!(sendingTermin || enriched || einsatzCtx || erinnerungenOffen);
  const zieht = useRef(false);
  useEffect(() => {
    if (!kalenderFenster) return undefined;
    const runter = () => { zieht.current = true; };
    const hoch = () => { zieht.current = false; };
    const aktualisiere = () => { if (!belegt.current && !zieht.current && document.visibilityState === "visible") load(true); };
    const sicht = () => { if (document.visibilityState === "visible") aktualisiere(); };
    window.addEventListener("pointerdown", runter, true);
    window.addEventListener("pointerup", hoch, true);
    window.addEventListener("pointercancel", hoch, true);
    window.addEventListener("focus", aktualisiere);
    document.addEventListener("visibilitychange", sicht);
    const takt = setInterval(aktualisiere, window.__kalenderTaktMs || 120000);   // __kalenderTaktMs: nur für den Test
    return () => {
      window.removeEventListener("pointerdown", runter, true);
      window.removeEventListener("pointerup", hoch, true);
      window.removeEventListener("pointercancel", hoch, true);
      window.removeEventListener("focus", aktualisiere);
      document.removeEventListener("visibilitychange", sicht);
      clearInterval(takt);
    };
  }, [kalenderFenster]); // eslint-disable-line

  // ---------- Seitenleiste rechts (Eingabe) und Planung ----------
  // Es gibt EINE Eingabe (TerminSeitenleiste) und EINE Planungsansicht (Kopfleiste PlanungsLeiste + Raster).
  // seite: null (zu) | { nr, modus, vorbelegung } (neu) | { nr, modus:"termin", termin } | { nr, modus:"aufgabe", aufgabe } (bearbeiten)
  const [suchParams, setSuchParams] = useSearchParams();
  const [seite, setSeite] = useState(null);
  const seitenNr = useRef(0);
  const [seitenModus, setSeitenModus] = useState("termin");   // Termin | Aufgabe, von der Seitenleiste gemeldet
  const [planWahl, setPlanWahl] = useState([]);                // gewählte Mitarbeiter (Kopfleiste und Seitenleiste teilen sie, filtern das Raster)
  const [zeitVorgabe, setZeitVorgabe] = useState(null);        // Klick/Ziehen/Vorschlag -> Zeit in die Seitenleiste
  const [entwurf, setEntwurf] = useState(null);                // Zeit, Titel, Kunde, Projekt des neuen Eintrags (Markierung im Raster, Kopfleiste)
  const [mobilOffen, setMobilOffen] = useState(false);          // Handy: Eingabe über den ganzen Bildschirm (bei neuer Planung erst nach der Zeitwahl)
  const neuePlanung = !!seite && !seite.termin && !seite.aufgabe;
  const kopfleiste = neuePlanung && !!seite.planung;           // Planung aus „+ Termin“/„+ Aufgabe“: Kopfleiste mit Mitarbeitern, Dauer, Nächste freie Zeit
  const pl = usePlanung({ modus: seitenModus, wahl: planWahl, onWahl: setPlanWahl, termine, abwesenheiten });

  // Kalender als Seite im selben Fenster (kalenderArt() = "seite"): „← zurück zu <Kunde>“ führt an die Ausgangsstelle (Browser-Verlauf)
  const navigate = useNavigate();
  const [ausgang, setAusgang] = useState(false);
  const zurueckZuAusgang = () => { setAusgang(false); navigate(-1); };
  const seiteZu = () => { setSeite(null); setZeitVorgabe(null); setEntwurf(null); setPlanWahl([]); if (ausgang) zurueckZuAusgang(); };
  const seiteAuf = (s, mobilGleichOffen = true) => { seitenNr.current += 1; setSeite({ nr: seitenNr.current, ...s }); setModus("kalender"); setZeitVorgabe(null); setEntwurf(null); setMobilOffen(mobilGleichOffen); };
  const seiteNeu = (v = {}) => {   // v: { modus, kunde_id, projekt_id, einsatz_id, ort, titel, monteure, start, ende }
    const m = v.modus === "aufgabe" ? "aufgabe" : "termin";
    setSeitenModus(m);
    setPlanWahl(v.monteure || (v.monteur ? [v.monteur] : []));
    seiteAuf({ modus: m, vorbelegung: v, planung: !v.start }, !!v.start);   // ohne Zeit: Planung (Kopfleiste), am Handy zuerst der Kalender
  };
  const termineBearbeiten = (t) => { setSeitenModus("termin"); setPlanWahl([]); seiteAuf({ modus: "termin", termin: t }); };
  const aufgabeBearbeiten = (a) => { setSeitenModus("aufgabe"); setPlanWahl([]); seiteAuf({ modus: "aufgabe", aufgabe: a }); };
  const ladeUndBearbeiten = async (art, id) => {   // Termin/Aufgabe aus der Liste, sonst vom Server holen
    let eintrag = (art === "termin" ? termine : aufgaben).find((x) => x.id === id);
    if (!eintrag) { try { eintrag = (await api.get(art === "termin" ? `/module-termine/${id}` : `/module-aufgaben/${id}`)).data; } catch { eintrag = null; } }
    if (!eintrag) { toast.error(art === "termin" ? "Der Termin wurde nicht gefunden." : "Die Aufgabe wurde nicht gefunden."); return; }
    if (art === "termin") termineBearbeiten(eintrag); else aufgabeBearbeiten(eintrag);
  };
  const aufgabeErledigt = async (a) => {   // Haken oben am Tag
    try { await api.patch(`/module-aufgaben/${a.id}/status`, { status: "erledigt" }); terminGeaendert(); toast.success(`Aufgabe „${a.titel}“ erledigt`); await load(true); }
    catch (err) { toast.error(err?.response?.data?.detail || "Konnte nicht als erledigt markiert werden"); }
  };
  const zeitWaehlen = (v) => {   // Zeit gewählt (Klick, Ziehen oder Vorschlag): in die offene neue Seitenleiste, sonst eine neue öffnen
    if (!v) return;
    pl.verwerfeVorschlag();
    if (neuePlanung) { setZeitVorgabe({ start: v.start, ende: v.ende, monteur: v.monteur || "", n: Date.now() }); setMobilOffen(true); return; }
    const kunde = selectedTarget?.type === "kunde" ? selectedTarget.id : (selectedTarget?.type === "projekt" ? (selectedTarget.kunde_id || "") : "");
    seiteNeu({ modus: "termin", kunde_id: kunde, projekt_id: selectedTarget?.type === "projekt" ? selectedTarget.id : "", start: v.start, ende: v.ende, monteure: v.monteur ? [v.monteur] : [] });
  };
  const zurueckNameF = () => kundeLabelById(seite?.vorbelegung?.kunde_id || seite?.termin?.kunde_id || seite?.aufgabe?.kunde_id || "") || "";
  const planBezug = () => [kundeLabelById(entwurf?.kunde_id), projekte.find(p => p.id === entwurf?.projekt_id)?.titel].filter(Boolean).join(" · ");

  // Kalender-Fenster: Werkbank des Projekts im Suite-Hauptfenster öffnen, sonst in einem normalen neuen Fenster/Tab
  const projektOeffnen = async (kundeId, projektId) => {
    if (await projektImHauptfenster(kundeId, projektId)) { toast.success("Projekt im Hauptfenster geöffnet"); return; }
    const url = werkbankAdresse(kundeId, projektId);
    let f = null;
    try { f = window.open(url, "graupner_suite_haupt"); } catch { f = null; }
    if (f) toast.success("Projekt in neuem Fenster geöffnet");
    else toast.warning("Der Browser hat das neue Fenster blockiert.", { duration: 15000, action: { label: "Jetzt öffnen", onClick: () => window.open(url, "graupner_suite_haupt") } });
  };
  // „+ Neuer Termin“: in der Kalenderansicht gleich hier (Seitenleiste), aus der Liste über die zentrale Stelle (Kalender)
  const neuAnlegen = () => {
    const kunde = selectedTarget?.type === "kunde" ? selectedTarget.id : (selectedTarget?.type === "projekt" ? (selectedTarget.kunde_id || "") : "");
    const projekt = selectedTarget?.type === "projekt" ? selectedTarget.id : "";
    if (modus === "kalender") { if (!neuePlanung) seiteNeu({ modus: "termin", kunde_id: kunde, projekt_id: projekt }); return; }
    terminPlanen({ modus: "termin", kunde_id: kunde, projekt_id: projekt });
  };
  // Wunsch von einem anderen Fenster (terminPlanen) oder aus der Adresse: Planung/Bearbeiten in der Seitenleiste
  const listenAktualisieren = async () => {   // Das Kalender-Fenster bleibt den ganzen Tag offen: neue Kunden/Projekte kennt es erst nach dem Nachladen
    const [k, p] = await Promise.all([api.get("/modules/kunden/data").catch(() => null), api.get("/module-projekte").catch(() => null)]);
    if (Array.isArray(k?.data)) setKunden(k.data);
    if (Array.isArray(p?.data)) setProjekte(p.data);
  };
  const wunschOeffnen = async (v) => {
    if ((v.kunde_id && !kunden.some((x) => x.id === v.kunde_id)) || (v.projekt_id && !projekte.some((x) => x.id === v.projekt_id))) await listenAktualisieren();
    if (v.termin_id) { ladeUndBearbeiten("termin", v.termin_id); return; }
    if (v.aufgabe_id) { ladeUndBearbeiten("aufgabe", v.aufgabe_id); return; }
    seiteNeu(v);
  };
  const wunschRef = useRef(wunschOeffnen);
  wunschRef.current = wunschOeffnen;
  useKalenderFenster((v) => {
    if (!kalenderFenster) return;
    if (v?.hinweis) { toast.info("Der Kalender ist hier schon offen."); return; }   // aus dem Seitenmenü „Kalender“
    wunschRef.current(v);
  }, kalenderFenster);

  // Adresse: /kalender?planen=1&modus=termin|aufgabe&kunde=..&projekt=..&monteure=a,b&einsatz=..&ort=..&titel=..&start=..&ende=..
  //          oder ?termin=<id> / ?aufgabe=<id> (bearbeiten, z. B. aus dem Einsatz)
  const planGeoeffnet = useRef(false);
  useEffect(() => {
    const q = (k) => suchParams.get(k) || "";
    if (q("kalender") && !kalenderFenster) { setModus("kalender"); setSuchParams({}, { replace: true }); return; }   // Seitenmenü „Kalender“ (Seite im Hauptfenster)
    if (!q("planen") && !q("termin") && !q("aufgabe")) { planGeoeffnet.current = false; return; }
    if (loading || planGeoeffnet.current) return;
    planGeoeffnet.current = true;
    if (q("zurueck") && !kalenderFenster) setAusgang(true);
    wunschOeffnen({
      modus: q("modus"), kunde_id: q("kunde"), projekt_id: q("projekt"), einsatz_id: q("einsatz"), ort: q("ort"), titel: q("titel"), start: q("start"), ende: q("ende"),
      monteure: q("monteure").split(",").map(x => x.trim()).filter(Boolean), termin_id: q("termin"), aufgabe_id: q("aufgabe"),
    });
    setSuchParams({}, { replace: true });
  }, [suchParams, loading]); // eslint-disable-line

  const waehleModus = (m) => { setModus(m); if (voll) return; try { localStorage.setItem(MODUS_KEY, m); } catch { /* egal */ } };

  // Urlaub/Krank für den Kalender (nur lesen). Eintrag erscheint nur, wenn der Mitarbeiter eindeutig einem Benutzer zugeordnet werden kann.
  useEffect(() => {
    if (modus !== "kalender" || !mitarbeiter.length) return undefined;
    let lebt = true;
    ladeAbwesenheiten(mitarbeiter).then((liste) => { if (lebt) setAbwesenheiten(liste); });
    return () => { lebt = false; };
  }, [modus, mitarbeiter]);

  // Kalender: Termin per Ziehen verschieben/verlängern – vorhandene PUT-Route; bei Fehler wirft die Funktion, der Kalender setzt zurück
  const verschiebeTermin = async (t, patch) => {
    await api.put(`/module-termine/${t.id}`, patch); terminGeaendert();
    await load();
  };

  const stats = useMemo(() => {
    const s = { wartet_auf_go: 0, bestaetigt: 0, im_kalender: 0, abgesagt: 0 };
    termine.forEach(t => { if (s[t.status] !== undefined) s[t.status] += 1; });
    return s;
  }, [termine]);

  // Helper: Kunden-Label und Such-Treffer
  const kundeLabelOf = (k) => k?.firma || [k?.vorname, k?.nachname].filter(Boolean).join(" ") || k?.name || k?.id || "";
  const kundeLabelById = (id) => {
    const k = kunden.find(x => x.id === id);
    return k ? kundeLabelOf(k) : null;
  };

  const searchHits = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (q.length < 1) return { kunden: [], projekte: [] };
    const ks = kunden
      .map(k => ({ id: k.id, label: kundeLabelOf(k) }))
      .filter(k => k.label.toLowerCase().includes(q))
      .slice(0, 8);
    const ps = projekte
      .map(p => ({ id: p.id, titel: p.titel || "(ohne Titel)", kunde_id: p.kunde_id, kundeLabel: kundeLabelById(p.kunde_id) }))
      .filter(p => p.titel.toLowerCase().includes(q) || (p.kundeLabel || "").toLowerCase().includes(q))
      .slice(0, 8);
    return { kunden: ks, projekte: ps };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, kunden, projekte]);

  const filteredTermine = useMemo(() => {
    let base = termine;
    if (selectedTarget) {
      if (selectedTarget.type === "kunde") {
        const projektIdsOfKunde = projekte.filter(p => p.kunde_id === selectedTarget.id).map(p => p.id);
        base = termine.filter(t =>
          t.kunde_id === selectedTarget.id || (t.projekt_id && projektIdsOfKunde.includes(t.projekt_id))
        );
      } else if (selectedTarget.type === "projekt") {
        base = termine.filter(t => t.projekt_id === selectedTarget.id);
      }
    }
    if (selectedMonteure.size > 0) {
      base = base.filter(t => monteureVon(t).some(m => selectedMonteure.has(m)));
    }
    return base;
  }, [termine, selectedTarget, projekte, selectedMonteure]);

  const uniqueMonteure = useMemo(() => {
    const set = new Set();
    termine.forEach(t => { monteureVon(t).forEach(m => set.add(m)); });
    return Array.from(set).sort();
  }, [termine]);

  const toggleMonteur = (username) => {
    setSelectedMonteure(prev => {
      const next = new Set(prev);
      if (next.has(username)) next.delete(username);
      else next.add(username);
      return next;
    });
  };

  const onGo = async (t) => {
    if (!(await terminBestaetigen(t, fmtDate(t.start)))) return;   // gemeinsame Stelle lib/terminGo.js (auch Kalender-Seitenleiste)
    setSendingTermin(t);  // Empfänger-Dialog öffnen
    load();
  };

  const onCancel = async (t) => {
    const grund = window.prompt(`Grund für die Absage von "${t.titel}"?`, "");
    if (grund === null) return;
    try {
      await api.patch(`/module-termine/${t.id}/cancel`, { status: "abgesagt", grund }); terminGeaendert();
      toast.success("Termin abgesagt");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Absage fehlgeschlagen");
    }
  };

  const onDelete = async (t) => {
    if (!window.confirm(`Termin "${t.titel}" endgültig löschen?`)) return;
    try {
      await api.delete(`/module-termine/${t.id}`); terminGeaendert();
      toast.success("Termin gelöscht");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Löschen fehlgeschlagen");
    }
  };

  const handleRowDrop = async (dropIdx) => {
    if (dragIndex === null || dragIndex === dropIdx) {
      setDragIndex(null); setDragOverIndex(null);
      return;
    }
    const reordered = [...filteredTermine];
    const [moved] = reordered.splice(dragIndex, 1);
    reordered.splice(dropIdx, 0, moved);

    // Optimistisch lokale Reihenfolge in `termine` übernehmen (nur die gefilterten Termine umsortieren)
    const reorderedIds = reordered.map(t => t.id);
    const otherTermine = termine.filter(t => !reorderedIds.includes(t.id));
    setTermine([...reordered, ...otherTermine]);
    setDragIndex(null); setDragOverIndex(null);

    try {
      await api.patch("/module-termine/reorder", { ids: reorderedIds }); terminGeaendert();
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Sortierung speichern fehlgeschlagen");
      load();
    }
  };

  const openEnriched = async (t) => {
    try {
      const res = await api.get(`/module-termine/${t.id}/enrich`);
      setEnriched(res.data);
    } catch (err) {
      toast.error("Datenmaske konnte nicht geladen werden");
    }
  };

  return (
    <div className={`${voll ? "max-w-none p-1 sm:p-2" : `${modus === "kalender" ? "max-w-[1700px]" : "max-w-6xl"} mx-auto p-4 lg:p-6`}`} data-testid="module-termine-page" data-modus={modus}>
      <div className={`flex items-center justify-between flex-wrap gap-2 ${voll ? "mb-2" : "mb-4"}`}>
        <div>
          <h1 className={`${voll ? "text-lg" : "text-2xl"} font-bold flex items-center gap-2`}>
            <Calendar className="w-6 h-6 text-primary" />
            Termine
          </h1>
          <p className={`text-sm text-muted-foreground mt-1 ${voll ? "hidden" : modus === "kalender" ? "hidden sm:block" : ""}`}>
            Ihre Termine mit Kunde, Projekt und Monteur. Im Kalender: Termin ziehen zum Verschieben.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="inline-flex border rounded-sm overflow-hidden" role="group" aria-label="Ansicht" data-testid="termine-modus">
            <button onClick={() => waehleModus("liste")} className={`h-9 px-3 text-sm flex items-center gap-1.5 ${modus === "liste" ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`} data-testid="btn-modus-liste">
              <ListIcon className="w-4 h-4" /> Liste
            </button>
            <button onClick={() => waehleModus("kalender")} className={`h-9 px-3 text-sm flex items-center gap-1.5 ${modus === "kalender" ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`} data-testid="btn-modus-kalender">
              <CalendarDays className="w-4 h-4" /> Kalender
            </button>
          </div>
          <button onClick={() => setErinnerungenOffen(true)} className="p-2 hover:bg-muted rounded-sm border" title="Erinnerungen an Monteure" aria-label="Erinnerungen" data-testid="btn-erinnerungen">
            <Bell className="w-4 h-4" />
          </button>
          {modus === "kalender" && (
            <button onClick={() => hilfeOeffnen("hilfe_termine")} className="p-2 hover:bg-muted rounded-sm border" title="Hilfe zum Kalender (F1)" aria-label="Hilfe" data-testid="btn-kalender-hilfe">
              <HelpCircle className="w-4 h-4" />
            </button>
          )}
          {voll ? (ausgang ? null : <a href="/dashboard" className="h-9 px-3 text-sm flex items-center gap-1.5 border rounded-sm hover:bg-muted" data-testid="link-zurueck-suite" title="Zurück zur Graupner Suite">
              <ChevronLeft className="w-4 h-4" /> zurück zur Suite
            </a>)
          : (
            <a href="/kalender" target="graupner_kalender" className="hidden md:flex h-9 px-3 text-sm items-center gap-1.5 border rounded-sm hover:bg-muted" data-testid="link-kalender-fenster" title="Kalender in eigenem Fenster öffnen (für einen zweiten Bildschirm)">
              <ExternalLink className="w-4 h-4" /> Kalender in eigenem Fenster
            </a>
          )}
          <button onClick={() => load()} className="p-2 hover:bg-muted rounded-sm border" title="Neu laden" data-testid="btn-termine-reload">
            <RefreshCw className="w-4 h-4" />
          </button>
          {modus === "liste" && <button
            onClick={() => neuAnlegen()}
            className="flex items-center gap-1 px-4 py-2 bg-primary text-primary-foreground rounded-sm hover:bg-primary/90"
            data-testid="btn-termin-create"
          >
            <Plus className="w-4 h-4" /> Neuer Termin
          </button>}
          {selectedTarget && (
            <button
              onClick={() => setEinsatzCtx(
                selectedTarget.type === "kunde"
                  ? { kundeId: selectedTarget.id }
                  : { kundeId: selectedTarget.kunde_id, projektId: selectedTarget.id, projektTitel: selectedTarget.label }
              )}
              className="flex items-center gap-1 px-4 py-2 bg-orange-50 text-orange-700 border border-orange-200 rounded-sm hover:bg-orange-100"
              data-testid="btn-termin-einsatz"
              disabled={selectedTarget.type === "projekt" && !selectedTarget.kunde_id}
            >
              <Wrench className="w-4 h-4" /> Neuer Einsatz
            </button>
          )}
        </div>
      </div>

      {/* Suchzeile: Kunde oder Projekt wählen */}
      <div className={`mb-4 ${voll && seite ? "hidden" : modus === "kalender" ? "hidden sm:block" : ""}`} data-testid="termine-search-section">
        {selectedTarget ? (
          <div className="flex items-center gap-2 p-3 bg-muted/40 border rounded-md" data-testid="termine-selected-target">
            {selectedTarget.type === "kunde" ? <UserIcon className="w-4 h-4 text-blue-700 dark:text-blue-300" /> : <Folder className="w-4 h-4 text-emerald-700 dark:text-emerald-300" />}
            <span className="text-sm font-medium">
              {selectedTarget.type === "kunde" ? "Kunde: " : "Projekt: "}{selectedTarget.label}
            </span>
            <button onClick={() => { setSelectedTarget(null); setSearchQuery(""); }} className="ml-auto p-1 hover:bg-background rounded-sm" title="Auswahl entfernen" data-testid="btn-termine-clear-target">
              <X className="w-4 h-4" />
            </button>
          </div>
        ) : (
          <div className="relative" {...sucheBox}>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                {...sucheInput}
                placeholder="Kunde oder Projekt suchen, um Termine anzuzeigen oder anzulegen …"
                className="w-full pl-10 pr-3 py-2 border rounded-md text-sm"
                data-testid="termine-search-input"
              />
            </div>
            {searchFocused && searchQuery.trim() && (searchHits.kunden.length > 0 || searchHits.projekte.length > 0) && (
              <div className="absolute left-0 right-0 top-full mt-1 bg-background border rounded-md shadow-lg z-20 max-h-80 overflow-auto" data-testid="termine-search-results" {...sucheListe}>
                {searchHits.kunden.length > 0 && (
                  <div>
                    <div className="px-3 py-1.5 text-xs font-semibold text-muted-foreground bg-muted/50 border-b">Kunden ({searchHits.kunden.length})</div>
                    {searchHits.kunden.map(k => (
                      <button key={`k-${k.id}`} onClick={() => { setSelectedTarget({ type: "kunde", id: k.id, label: k.label }); setSearchQuery(""); }} className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm hover:bg-muted border-b last:border-b-0" data-testid={`termine-hit-kunde-${k.id}`}>
                        <UserIcon className="w-4 h-4 text-blue-700 dark:text-blue-300 flex-shrink-0" />
                        <span>{k.label}</span>
                      </button>
                    ))}
                  </div>
                )}
                {searchHits.projekte.length > 0 && (
                  <div>
                    <div className="px-3 py-1.5 text-xs font-semibold text-muted-foreground bg-muted/50 border-b">Projekte ({searchHits.projekte.length})</div>
                    {searchHits.projekte.map(p => (
                      <button key={`p-${p.id}`} onClick={() => { setSelectedTarget({ type: "projekt", id: p.id, label: p.titel, kunde_id: p.kunde_id }); setSearchQuery(""); }} className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm hover:bg-muted border-b last:border-b-0" data-testid={`termine-hit-projekt-${p.id}`}>
                        <Folder className="w-4 h-4 text-emerald-700 flex-shrink-0" />
                        <span>{p.titel}</span>
                        {p.kundeLabel && <span className="text-xs text-muted-foreground ml-auto">({p.kundeLabel})</span>}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
            {searchFocused && searchQuery.trim() && searchHits.kunden.length === 0 && searchHits.projekte.length === 0 && (
              <div className="absolute left-0 right-0 top-full mt-1 bg-background border rounded-md shadow-lg z-20 p-3 text-sm text-muted-foreground" data-testid="termine-search-empty">
                Keine Treffer für „{searchQuery}".
              </div>
            )}
          </div>
        )}
      </div>

      {/* Stats-Kacheln (nur in der Liste; im Kalender zeigt die Legende die Zeichen) */}
      {modus === "liste" && <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        {Object.keys(STATUS_STYLES).map(key => {
          const Icon = STATUS_STYLES[key].icon;
          return (
            <button
              key={key}
              onClick={() => setFilterStatus(filterStatus === key ? "" : key)}
              className={`border rounded-md p-3 text-left transition-colors ${
                filterStatus === key
                  ? STATUS_STYLES[key].cls + " ring-2 ring-offset-1 ring-current"
                  : "bg-background text-foreground hover:bg-muted/50"
              }`}
              data-testid={`stat-${key}`}
            >
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Icon className="w-3.5 h-3.5" /> {STATUS_LABEL[key]}
              </div>
              <div className="text-2xl font-bold mt-1 text-foreground">{stats[key]}</div>
            </button>
          );
        })}
      </div>}

      {filterStatus && (
        <div className="text-xs text-muted-foreground mb-3 flex items-center gap-2">
          <Filter className="w-3.5 h-3.5" /> Filter: {STATUS_LABEL[filterStatus]}
          <button onClick={() => setFilterStatus("")} className="text-primary hover:underline">zurücksetzen</button>
        </div>
      )}

      {/* Kalender (A-027 S1): Raster links, Seitenleiste (Eingabe) rechts – nichts legt sich über den Kalender */}
      {modus === "kalender" && (
        <div className="flex gap-3 items-start" data-testid="kalender-mit-seitenleiste">
        <div className="flex-1 min-w-0">
        {ausgang && (
          <button type="button" onClick={zurueckZuAusgang} className="mb-2 h-10 px-4 flex items-center gap-1.5 text-sm font-semibold rounded-sm border-2 border-primary text-primary hover:bg-primary/5 max-w-full" data-testid="btn-kalender-zurueck">
            <ChevronLeft className="w-4 h-4 shrink-0" /> <span className="truncate">zurück{zurueckNameF() ? ` zu ${zurueckNameF()}` : ""}</span>
          </button>
        )}
        {kopfleiste && (
          <PlanungsLeiste
            modus={seitenModus} bezug={planBezug()}
            mitarbeiter={mitarbeiter} wahl={planWahl} abwesenheiten={abwesenheiten} plan={pl}
            onUebernehmen={zeitWaehlen} onEingabe={() => setMobilOffen(true)}
          />
        )}
        <TerminKalender
          auswahl={kopfleiste}
          rasterAbzug={kopfleiste ? 330 : 250}
          markierung={kopfleiste ? pl.vorschlag : null}
          entwurf={neuePlanung && entwurf?.start ? entwurf : null}
          springeZu={kopfleiste ? pl.springe : null}
          onDatum={kopfleiste ? (d) => { pl.angezeigt.current = d; } : null}
          termine={kopfleiste ? pl.sichtbareTermine : filteredTermine}
          laden={loading}
          kunden={kunden}
          projekte={projekte}
          benutzer={mitarbeiter}
          abwesenheiten={abwesenheiten}
          legende={uniqueMonteure}
          gewaehlt={kopfleiste ? new Set(planWahl) : selectedMonteure}
          onMonteurUmschalten={toggleMonteur}
          onOeffnen={termineBearbeiten}
          onNeu={(v) => (v ? zeitWaehlen(v) : neuAnlegen())}
          onAendern={verschiebeTermin}
          aufgaben={aufgaben}
          onOeffnenAufgabe={aufgabeBearbeiten}
          onAufgabeErledigt={aufgabeErledigt}
        />
        </div>
        {seite && (
          <TerminSeitenleiste
            key={seite.nr}
            termin={seite.termin || null}
            aufgabe={seite.aufgabe || null}
            modus={seite.modus}
            kunden={kunden}
            projekte={projekte}
            aufgaben={aufgaben}
            mitarbeiter={mitarbeiter}
            selectedTarget={neuePlanung ? selectedTarget : null}
            vorbelegung={seite.vorbelegung || null}
            termine={termine}
            zeitVorgabe={zeitVorgabe}
            monteureExtern={kopfleiste ? planWahl : null}
            onMonteure={kopfleiste ? setPlanWahl : null}
            onModus={setSeitenModus}
            onEntwurf={neuePlanung ? setEntwurf : null}
            onProjektOeffnen={kalenderFenster ? projektOeffnen : null}
            mobilVersteckt={kopfleiste && !mobilOffen}
            onZurueck={kopfleiste ? () => setMobilOffen(false) : seiteZu}
            onClose={seiteZu}
            onSaved={() => { seiteZu(); load(); }}
          />
        )}
        </div>
      )}

      {/* Liste */}
      {modus === "liste" && (loading ? (
        <div className="text-center py-12 text-muted-foreground">Lade Termine…</div>
      ) : filteredTermine.length === 0 ? (
        <div className="text-center py-16 border-2 border-dashed rounded-md text-muted-foreground" data-testid="empty-state">
          <Calendar className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p>
            {selectedMonteure.size > 0
              ? "Keine weiteren Termine für diesen Monteur vorhanden."
              : selectedTarget
                ? `Keine Termine für ${selectedTarget.type === "kunde" ? "diesen Kunden" : "dieses Projekt"}${filterStatus ? " in diesem Status" : ""}.`
                : "Keine Termine vorhanden."}
          </p>
          {selectedTarget && <p className="text-xs mt-1">Klicke oben rechts auf „+ Neuer Termin".</p>}
        </div>
      ) : (
        <div className="space-y-2" data-testid="termine-list">
          {uniqueMonteure.length > 0 && (
            <div className="flex items-center gap-2 flex-wrap pb-2 mb-1 border-b" data-testid="monteur-filter-bar">
              <span className="text-xs text-muted-foreground font-medium">Monteur-Filter:</span>
              <button
                type="button"
                onClick={() => {
                  setSelectedMonteure(new Set());
                  toast.success(`Alle Termine werden angezeigt (${filteredTermine.length} gesamt)`);
                }}
                className={`px-2 py-1 rounded-full border text-xs transition-all ${selectedMonteure.size === 0 ? "bg-foreground text-background border-transparent shadow" : "bg-background border-border hover:bg-muted"}`}
                data-testid="monteur-filter-alle"
              >
                Alle
              </button>
              {uniqueMonteure.map(u => {
                const active = selectedMonteure.has(u);
                const c = colorForUser(u);
                return (
                  <button
                    key={u}
                    type="button"
                    onClick={() => toggleMonteur(u)}
                    className={`flex items-center gap-2 px-2 py-1 rounded-full border text-xs transition-all ${active ? `${c.bg} text-white border-transparent shadow` : "bg-background border-border hover:bg-muted"}`}
                    title={monteurName(u)}
                    data-testid={`monteur-filter-${u}`}
                  >
                    <span className={`w-5 h-5 rounded-full ${c.bg} text-white text-[10px] font-bold flex items-center justify-center`}>
                      {initialsOf(u)}
                    </span>
                    <span className={active ? "" : "text-foreground"}>{monteurName(u)}</span>
                  </button>
                );
              })}

            </div>
          )}
          {filteredTermine.map((t, idx) => {
            const styles = STATUS_STYLES[t.status] || STATUS_STYLES.wartet_auf_go;
            const Icon = styles.icon;
            const kundeName = kunden.find(k => k.id === t.kunde_id);
            const projektTitel = projekte.find(p => p.id === t.projekt_id)?.titel;
            const monteurColor = colorForUser(monteureVon(t)[0]);
            return (
              <div
                key={t.id}
                draggable
                onDragStart={() => setDragIndex(idx)}
                onDragOver={(e) => { e.preventDefault(); setDragOverIndex(idx); }}
                onDragLeave={() => setDragOverIndex(null)}
                onDrop={() => handleRowDrop(idx)}
                onDragEnd={() => { setDragIndex(null); setDragOverIndex(null); }}
                className={`border border-l-4 rounded-md p-3 bg-background hover:shadow-sm transition-shadow ${monteurColor ? monteurColor.border : "border-l-transparent"} ${dragOverIndex === idx ? "border-primary/50 bg-primary/5" : ""} ${dragIndex === idx ? "opacity-40" : ""}`}
                data-testid={`termin-${t.id}`}
              >
                <div className="flex items-start gap-3">
                  <div className="cursor-grab active:cursor-grabbing p-1 text-muted-foreground/40 hover:text-muted-foreground flex-shrink-0" title="Zum Sortieren ziehen" data-testid={`drag-handle-${t.id}`}>
                    <GripVertical className="w-4 h-4" />
                  </div>
                  <div className={`p-2 rounded-sm border flex-shrink-0 ${styles.cls}`}>
                    <Icon className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-semibold">{t.titel}</h3>
                      <span className={`text-xs px-2 py-0.5 rounded-sm border ${styles.cls}`}>
                        {STATUS_LABEL[t.status]}
                      </span>
                      <span className="text-xs text-muted-foreground">{typBezeichnung(t.typ)}</span>
                    </div>
                    {t.beschreibung?.trim() && (
                      <p className="text-sm text-foreground mt-1 line-clamp-2" data-testid={`termin-beschreibung-${t.id}`}>
                        {t.beschreibung}
                      </p>
                    )}
                    <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground flex-wrap">
                      <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {fmtDate(t.start)}</span>
                      {t.ende && <span>– {fmtDate(t.ende)}</span>}
                      {t.ort && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {t.ort}</span>}
                    </div>
                    <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground flex-wrap">
                      {kundeName && (
                        <span className="flex items-center gap-1">
                          <UserIcon className="w-3 h-3" />
                          {`${kundeName.vorname || ""} ${kundeName.nachname || ""}`.trim() || kundeName.name || "—"}
                        </span>
                      )}
                      {projektTitel && (
                        <span className="flex items-center gap-1"><Folder className="w-3 h-3" /> {projektTitel}</span>
                      )}
                      {monteureVon(t).map(m => (
                        <span key={m} className="flex items-center gap-1.5">
                          <MonteurAvatar username={m} title={monteurName(m)} />
                          <span>{monteurName(m)}</span>
                        </span>
                      ))}
                    </div>
                    {t.status === "abgesagt" && t.abgesagt_grund && (
                      <p className="text-xs text-red-600 dark:text-red-300 mt-1">Grund: {t.abgesagt_grund}</p>
                    )}
                    {t.status === "im_kalender" && t.google_event_id && (
                      <p className="text-xs text-emerald-600 dark:text-emerald-300 mt-1">📅 Google Event ID: {t.google_event_id}</p>
                    )}
                  </div>
                  <div className="flex flex-col gap-1 flex-shrink-0">
                    {t.status === "wartet_auf_go" && (
                      <button
                        onClick={() => onGo(t)}
                        className="px-3 py-1.5 bg-emerald-600 text-white rounded-sm text-xs font-bold hover:bg-emerald-700"
                        data-testid={`btn-go-${t.id}`}
                      >
                        ✓ GO
                      </button>
                    )}
                    {(t.status === "bestaetigt" || t.status === "im_kalender") && (
                      <button
                        onClick={() => setSendingTermin(t)}
                        className="px-3 py-1.5 border border-blue-300 text-blue-700 rounded-sm text-xs hover:bg-blue-50 flex items-center justify-center gap-1"
                        data-testid={`btn-send-${t.id}`}
                      >
                        📧 ICS senden
                      </button>
                    )}
                    {t.status === "abgesagt" && (
                      <button
                        onClick={() => onGo(t)}
                        className="px-3 py-1.5 bg-emerald-600 text-white rounded-sm text-xs hover:bg-emerald-700"
                      >
                        Reaktivieren
                      </button>
                    )}
                    <button
                      onClick={() => openEnriched(t)}
                      className="text-xs px-2 py-1 border border-border bg-background text-foreground rounded-sm hover:bg-muted flex items-center gap-1"
                      data-testid={`btn-enrich-${t.id}`}
                    >
                      Datenmaske <ChevronRight className="w-3 h-3" />
                    </button>
                    <button
                      onClick={() => terminPlanen({ termin_id: t.id })}
                      className="text-xs px-2 py-1 border border-border bg-background text-foreground rounded-sm hover:bg-muted"
                      data-testid={`btn-edit-${t.id}`}
                    >
                      Bearbeiten
                    </button>
                    {t.status !== "abgesagt" && (
                      <button onClick={() => onCancel(t)} className="text-xs px-2 py-1 border border-red-200 dark:border-red-900 text-red-700 dark:text-red-300 rounded-sm hover:bg-red-50 dark:hover:bg-red-950/40">
                        Absagen
                      </button>
                    )}
                    <button
                      onClick={() => onDelete(t)}
                      className="p-1 text-red-500 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/40 rounded-sm border border-transparent hover:border-red-200 dark:hover:border-red-900"
                      title="Löschen"
                      data-testid={`btn-delete-${t.id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ))}

      {erinnerungenOffen && <ErinnerungenDialog onClose={() => setErinnerungenOffen(false)} />}

      {enriched && <EnrichedView data={enriched} onClose={() => setEnriched(null)} />}

      {sendingTermin && (
        <TerminSendDialog
          termin_id={sendingTermin.id}
          onClose={() => setSendingTermin(null)}
          onSent={() => { setSendingTermin(null); load(); }}
        />
      )}

      <EinsatzModal
        open={!!einsatzCtx}
        context={einsatzCtx || {}}
        onClose={() => setEinsatzCtx(null)}
        onSaved={() => {}}
      />
    </div>
  );
}

const EnrichedView = ({ data, onClose }) => {
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="enriched-dialog">
      <div className="bg-background rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-semibold">Datenmaske: {data.titel}</h2>
          <button onClick={onClose} className="p-1 hover:bg-muted rounded-sm"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-4 space-y-4 text-sm">
          <Block title="Termin (module_termine)" icon={Calendar}>
            <Field label="Titel" value={data.titel} />
            <Field label="Typ" value={typBezeichnung(data.typ)} />
            <Field label="Start" value={fmtDate(data.start)} />
            <Field label="Ende" value={fmtDate(data.ende) || "—"} />
            <Field label="Ort" value={data.ort || "—"} />
            <Field label="Status" value={STATUS_LABEL[data.status]} />
          </Block>

          <Block title="Kunde (module_kunden)" icon={UserIcon}>
            {data.kunde_detail ? (
              <>
                <Field label="Name" value={`${data.kunde_detail.vorname || ""} ${data.kunde_detail.nachname || ""}`.trim() || data.kunde_detail.name} />
                <Field label="Firma" value={data.kunde_detail.firma || "—"} />
                <Field label="E-Mail" value={data.kunde_detail.email || "—"} />
                <Field label="Telefon" value={data.kunde_detail.phone || "—"} />
                <Field
                  label="Adresse"
                  value={
                    [data.kunde_detail.strasse, data.kunde_detail.hausnummer, data.kunde_detail.plz, data.kunde_detail.ort]
                      .filter(Boolean)
                      .join(" ") || "—"
                  }
                />
              </>
            ) : (
              <p className="text-muted-foreground italic">— Kein Kunde verknüpft —</p>
            )}
          </Block>

          <Block title="Projekt (module_projekte)" icon={Folder}>
            {data.projekt_detail ? (
              <>
                <Field label="Titel" value={data.projekt_detail.titel} />
                <Field label="Kategorie" value={data.projekt_detail.kategorie || "—"} />
                <Field label="Projekt-Status" value={data.projekt_detail.status || "—"} />
              </>
            ) : (
              <p className="text-muted-foreground italic">— Kein Projekt verknüpft —</p>
            )}
          </Block>

          <Block title="Aufgabe (module_aufgaben)" icon={Briefcase}>
            {data.aufgabe_detail ? (
              <>
                <Field label="Titel" value={data.aufgabe_detail.titel} />
                <Field label="Kategorie" value={data.aufgabe_detail.kategorie} />
                <Field label="Priorität" value={data.aufgabe_detail.prioritaet} />
                <Field label="Status" value={data.aufgabe_detail.status} />
              </>
            ) : (
              <p className="text-muted-foreground italic">— Keine interne Aufgabe verknüpft —</p>
            )}
          </Block>

          <Block title="Monteur" icon={HardHat}>
            {data.monteur_detail ? (
              <>
                <Field label="Name" value={data.monteur_detail.anzeige_name} />
                <Field label="Login" value={data.monteur_detail.username} />
                <Field label="Rolle" value={data.monteur_detail.role} />
              </>
            ) : (
              <p className="text-muted-foreground italic">— Niemand zugewiesen —</p>
            )}
          </Block>
        </div>
      </div>
    </div>
  );
};

const Block = ({ title, icon: Icon, children }) => (
  <div className="border rounded-md p-3 bg-muted/20">
    <h4 className="font-semibold text-sm mb-2 flex items-center gap-2">
      <Icon className="w-4 h-4 text-primary" /> {title}
    </h4>
    <div className="space-y-1">{children}</div>
  </div>
);

const Field = ({ label, value }) => (
  <div className="flex items-baseline gap-2 text-sm">
    <span className="text-muted-foreground w-24 flex-shrink-0">{label}:</span>
    <span className="font-medium">{value || "—"}</span>
  </div>
);
